import React, { useEffect, useRef, useState } from "react";

export function Field({ label, hint, right, children, testId }) {
  return (
    <div className="fo-field" data-testid={testId}>
      {label && (
        <div className="fo-label">
          {label}
          {right && <span>{right}</span>}
        </div>
      )}
      {children}
      {hint && <p className="fo-hint">{hint}</p>}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, className = "", testId, type = "text" }) {
  return (
    <input
      type={type}
      className={`fo-input ${className}`}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(type === "number" ? Number(e.target.value || 0) : e.target.value)}
      data-testid={testId}
    />
  );
}

export function TextArea({ value, onChange, placeholder, testId, rows = 2 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.max(el.scrollHeight, 48)}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={rows}
      className="fo-ta"
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    />
  );
}

export function Pills({ options, value, onChange, small, testId }) {
  return (
    <div className="fo-pills" role="radiogroup" data-testid={testId}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.v;
        const l = typeof o === "string" ? o : o.l;
        const on = value === v;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={on}
            className={`fo-pill ${small ? "sm" : ""} ${on ? "on" : ""}`}
            onClick={() => onChange(on ? null : v)}
            data-testid={testId ? `${testId}-${String(v).replace(/\s+/g, "-").toLowerCase()}` : undefined}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

export function MultiPills({ options, value = [], onChange, small, testId }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="fo-pills" data-testid={testId}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.v;
        const l = typeof o === "string" ? o : o.l;
        const on = value.includes(v);
        return (
          <button
            key={v}
            type="button"
            aria-pressed={on}
            className={`fo-pill ${small ? "sm" : ""} ${on ? "on" : ""}`}
            onClick={() => toggle(v)}
            data-testid={testId ? `${testId}-${String(v).replace(/\s+/g, "-").toLowerCase()}` : undefined}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({ on, onChange, label, testId }) {
  return (
    <button type="button" className={`fo-toggle ${on ? "on" : ""}`} aria-pressed={!!on} onClick={() => onChange(!on)} data-testid={testId}>
      <span className="tk" />
      {label && <span>{label}</span>}
    </button>
  );
}

export function Chips({ value = [], onChange, placeholder = "Add and press Enter", testId }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const v = draft.trim();
    if (!v || value.includes(v)) return setDraft("");
    onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className="fo-chips" data-testid={testId}>
      {value.map((c, i) => (
        <span className="fo-chip" key={`${c}-${i}`} data-testid={testId ? `${testId}-chip-${i}` : undefined}>
          {c}
          <button type="button" aria-label={`Remove ${c}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button>
        </span>
      ))}
      <span className="fo-chip-add">
        <input
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          onBlur={commit}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </span>
    </div>
  );
}

export function Reveal({ when, children }) {
  if (!when) return null;
  return <div className="fo-reveal">{children}</div>;
}

export function AddButton({ onClick, children, testId }) {
  return (
    <button type="button" className="fo-add" onClick={onClick} data-testid={testId}>
      <span aria-hidden>+</span> {children}
    </button>
  );
}

export function RemoveButton({ onClick, label = "Remove", testId }) {
  return (
    <button type="button" className="fo-x" aria-label={label} onClick={onClick} data-testid={testId}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
    </button>
  );
}

export function Tag({ tone = "mut", children, testId }) {
  return <span className={`fo-tag ${tone}`} data-testid={testId}>{children}</span>;
}

export const patchAt = (list, i, patch) => list.map((x, j) => (j === i ? { ...x, ...patch } : x));
export const removeAt = (list, i) => list.filter((_, j) => j !== i);
